import {combine, merge, sample} from 'effector';
import i18next from 'i18next';
import {toast} from 'react-toastify';

import {showRestError} from '@/app/errors';
import {rootDomain} from '@/app/model';

import {CreateEmployeeFormValues} from '@/pages/EployersControl/CreateEmployeeModal/schema';

import {createGridTableModel, initFiltersTable, initSortingsTable} from '@shared/composables/table';
import {mergeEffects} from '@shared/lib/effector/merge-effects';

import {userModel} from '../user/model';

import * as api from './api';
import {
    Employee,
    EmployeesRequest,
    EmployeesResponse,
    ResetPasswordRequest,
    UpdateEmployeeRequest,
} from './type';

const domain = rootDomain.createDomain('Employees');

export const fetchEmployeesFx = domain.createEffect(api.fetchEmployees);
export const fetchInspectorsFx = domain.createEffect(api.fetchInspectors);
export const fetchUsersFx = domain.createEffect(api.fetchUsers);

const fetchEmployeesCombined = mergeEffects([fetchEmployeesFx, fetchInspectorsFx, fetchUsersFx]);

const grid = createGridTableModel({domain});

export const {
    filters$,
    filtersChanged,
    filtersReset,
    defaultFilters,
    sortings$,
    sortingsChanged,
    sortingsReset,
    defaultSortings,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    Gate: EmployeesGate,
    tableStateChanged,
    stateReset,
} = grid;

export const createEmployeeFx = domain.createEffect(api.createEmployee);
export const createInspectorFx = domain.createEffect(api.createInspector);
export const createChiefInspectorFx = domain.createEffect(api.createChiefInspector);
const createCombined = mergeEffects([createEmployeeFx, createInspectorFx, createChiefInspectorFx]);

export const doCreateEmployee = domain.createEvent<CreateEmployeeFormValues>();
createCombined.doneData.watch(() => {
    toast.success(i18next.t('page_employers_control.create_employee.success'));
    setCreateEmployeeModal(false);
});
createCombined.failData.watch(showRestError);

const createEmployeeFn = (payload: CreateEmployeeFormValues) => ({
    userName: payload.email,
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    ...(payload.taxAuthorityDepartment && {
        taxAuthorityDepartment: payload.taxAuthorityDepartment.value,
    }),
    ...(payload.middleName && {middleName: payload.middleName}),
    ...(payload.phone && {phone: payload.phone}),
});
sample({
    clock: doCreateEmployee,
    fn: createEmployeeFn,
    filter: userModel.isDirector,
    target: createEmployeeFx,
});
sample({
    clock: doCreateEmployee,
    fn: createEmployeeFn,
    filter: userModel.isChiefInspector,
    target: createInspectorFx,
});
sample({
    clock: doCreateEmployee,
    fn: createEmployeeFn,
    filter: userModel.isOperator,
    target: createChiefInspectorFx,
});

export const setCreateEmployeeModal = domain.createEvent<boolean>();
export const $createEmployeeModal = domain
    .createStore<boolean>(false)
    .on(setCreateEmployeeModal, (_, value) => value)
    .reset(EmployeesGate.close);

export const setEditEmployeeModal = domain.createEvent<boolean>();
export const $editEmployeeModal = domain
    .createStore<boolean>(false)
    .on(setEditEmployeeModal, (_, value) => value)
    .reset(EmployeesGate.close);

export const setSelectedEmployeeForEdit = domain.createEvent<Employee | null>();
export const $selectedEmployeeForEdit = domain
    .createStore<Employee | null>(null)
    .on(setSelectedEmployeeForEdit, (_, employee) => employee)
    .reset(EmployeesGate.close);

sample({
    clock: setEditEmployeeModal,
    filter: (open): open is false => !open,
    fn: () => null,
    target: setSelectedEmployeeForEdit,
});

export const updateEmployeeFx = domain.createEffect(api.updateEmployee);
export const doUpdateEmployee = domain.createEvent<UpdateEmployeeRequest>();
sample({
    clock: doUpdateEmployee,
    target: updateEmployeeFx,
});

export const resendEmployeeInvitation = domain.createEvent<ResetPasswordRequest>();
export const resendEmployeeInvitationFx = domain.createEffect(api.resendInvitation);
sample({
    clock: resendEmployeeInvitation,
    target: resendEmployeeInvitationFx,
});

export const resetEmployeePassword = domain.createEvent<ResetPasswordRequest>();
export const resetPasswordFx = domain.createEffect(api.resetPassword);
sample({
    clock: resetEmployeePassword,
    target: resetPasswordFx,
});

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

const data$ = domain.createStore<EmployeesResponse | null>(null);

const searchChanged = domain.createEvent<string>();
const search$ = domain.createStore('').on(searchChanged, (_, value) => value);

pageNumber$.reset(filtersChanged, sortingsChanged, stateReset);
pageSize$.reset(stateReset);

data$.on(fetchEmployeesCombined.doneData, (_, data) => data).reset(EmployeesGate.close);

const reloadClock = merge([
    EmployeesGate.open,
    tableStateChanged,
    searchChanged,
    filtersChanged,
    createCombined.doneData,
    updateEmployeeFx.doneData,
]);

const $employeesRequest = combine({
    role: userModel.role,
    lpId: userModel.state.map((s) => s.profile?.company.id ?? undefined),
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    order: sortings$,
    filters: filters$,
});

const toRequest = ({
    lpId,
    pageSize,
    pageNumber,
    order,
    filters,
}: {
    lpId: number | undefined;
    pageSize: number;
    pageNumber: number;
    order: Record<string, boolean | undefined>;
    filters?: any;
}): EmployeesRequest => {
    console.log('filters', filters);
    const sortingData = Object.entries(order);
    const sortingList = sortingData.map(([key]) => {
        if (key === 'userStatus') return 'isActive';
        return key;
    });
    const sortingDirection =
        sortingData.length > 0 && sortingData[0][1] !== undefined
            ? sortingData[0][1]
                ? 'ASC'
                : 'DESC'
            : undefined;

    const filtersData = {
        ...(filters?.role && {role: filters.role.value}),
        ...(filters?.email && {email: filters.email}),
        ...(filters?.company && {companyName: filters.company}),
        ...(filters?.phone && {phone: filters.phone}),
    };
    return {
        ...(lpId && {lpId}),
        size: pageSize,
        number: pageNumber,
        ...(sortingList.length && {sortList: sortingList}),
        ...(sortingDirection && {sortDirection: sortingDirection}),
        ...filtersData,
    };
};

sample({
    clock: reloadClock,
    source: $employeesRequest,
    filter: userModel.isDirector,
    fn: toRequest,
    target: fetchEmployeesFx,
});

sample({
    clock: reloadClock,
    source: $employeesRequest,
    filter: userModel.isChiefInspector,
    fn: toRequest,
    target: fetchInspectorsFx,
});

sample({
    clock: reloadClock,
    source: $employeesRequest,
    filter: userModel.isOperator,
    fn: toRequest,
    target: fetchUsersFx,
});

const $employeesState = combine({
    data: data$,
    pageCount: data$.map((data) => data?.pageCount ?? 0),
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    filters: filters$,
    sortings: sortings$,
    search: search$,
    pending: fetchEmployeesCombined.$pending,
});

export const employeesModel = {
    state: $employeesState,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    sortingsReset,
    pageNumberChanged,
    pageSizeChanged,
    searchChanged,
};

export const $employeePending = combine(
    createCombined.$pending,
    updateEmployeeFx.pending,
    resendEmployeeInvitationFx.pending,
    resetPasswordFx.pending,
    (createCombined, updateEmployeeFx, resendEmployeeInvitationFx, resetPasswordFx) =>
        createCombined || updateEmployeeFx || resendEmployeeInvitationFx || resetPasswordFx,
);

updateEmployeeFx.doneData.watch(() => {
    toast.success(i18next.t('page_employers_control.edit_employee.success'));
    setEditEmployeeModal(false);
});

updateEmployeeFx.failData.watch(showRestError);
resendEmployeeInvitationFx.doneData.watch(() => {
    toast.success(i18next.t('page_employers_control.resend_invitation.success'));
    setEditEmployeeModal(false);
});
resendEmployeeInvitationFx.failData.watch(showRestError);
resetPasswordFx.doneData.watch(() => {
    toast.success(i18next.t('page_employers_control.reset_password.success'));
    setEditEmployeeModal(false);
});
resetPasswordFx.failData.watch(showRestError);
